<script setup lang="ts">
import type Button from '@/components/auth/verification/Box/Proceed.vue';
import type { BankAccount } from '@/types/UserData';
import { BankAccountType } from '@/types/UserData';
import { cpfMask } from '~/utils/validateCpf';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isCaptchaRegisterEnabled } = useCaptcha();
const { userDocument, userName, fetchUserProfile } = useUserData();
const { fetchBankList } = usePayments();

const isLoading = ref(false);
const proceedButtonRef = ref<InstanceType<typeof Button> | null>(null);
const bankOptions = ref<Bank[]>([]);
const bankAccountData = reactive<Partial<BankAccount>>({
  number: '',
  agency: '',
  number_digit: '',
  agency_digit: '',
  bank_id: 0,
  type: BankAccountType.CORRENTE,
});

const userAccountData = reactive({
  document_number: userDocument.value,
  owner_name: userName.value,
});

const antiRobotToken = computed(
  () => proceedButtonRef.value?.antiRobotToken ?? '',
);

const accountTypeConfig = {
  [BankAccountType.CORRENTE]: { label: 'Corrente', value: 1 },
  [BankAccountType.POUPANCA]: { label: 'Poupança', value: 2 },
  [BankAccountType.SALARIO]: { label: 'Salário', value: 3 },
} as const;

const accountTypeOptions = computed(() =>
  Object.entries(accountTypeConfig).map(([key, config]) => ({
    value: key,
    label: config.label,
  })),
);

const isProceedDisabled = computed(() => {
  if (isLoading.value) return true;
  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) return true;

  return false;
});

const formIsValid = computed(() => {
  if (
    !bankAccountData.number ||
    !bankAccountData.agency ||
    !bankAccountData.number_digit ||
    !bankAccountData.bank_id ||
    !bankAccountData.type
  ) {
    return false;
  }

  return true;
});

function handleSubmit() {
  if (isProceedDisabled.value || !formIsValid.value) return;

  const payload = {
    bank_account: {
      number: bankAccountData.number,
      agency: bankAccountData.agency,
      number_digit: bankAccountData.number_digit,
      agency_digit: bankAccountData.agency_digit,
      document_number: userAccountData.document_number,
      owner_name: userAccountData.owner_name,
      type: accountTypeConfig[bankAccountData.type!].value,
      bank_id: bankAccountData.bank_id,
    },
  };

  handleBankAccount(payload)
    .then((res: any) => {
      openToaster({
        message: res.data?.message ?? t('account.bank_section.update_success'),
        type: 'success',
      });
    })
    .catch((error: any) => {
      openToaster({
        message:
          error.data?.message ??
          error.message ??
          t('account.bank_section.generic_error'),
        type: 'error',
      });
    });
}

async function handleBankAccount(payload: any) {
  const { ApiService } = useApiService();

  isLoading.value = true;

  try {
    const res = await ApiService.patch(
      '/bff/users/update-pending-data',
      payload,
    );

    await fetchUserProfile({ force: true });
    await $containment.solve({
      name: 'bank_account',
      customValidator: () => res.data.success,
    });

    return res;
  } finally {
    isLoading.value = false;
  }
}

function sanitizeInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const field = input.id as keyof Pick<
    BankAccount,
    'agency' | 'agency_digit' | 'number' | 'number_digit'
  >;

  if (field === 'number_digit') {
    bankAccountData[field] = input.value.replace(/[^A-Za-z0-9]/g, '');

    return;
  }

  bankAccountData[field] = input.value.replace(/\D/g, '');
}

onMounted(async () => {
  const bank = await fetchBankList();

  bankOptions.value = (bank?.data ?? []).map((bankItem: Bank) => ({
    value: bankItem.id,
    label: `${bankItem.code} - ${bankItem.name}`,
  }));
});
</script>

<template>
  <AuthVerificationBox
    :description="$t('account.bank_section.description')"
    :is-loading="isLoading"
  >
    <div :class="$style.container">
      <section :class="$style.sectionContainer">
        <section :class="$style.fieldsRow">
          <div :class="$style.fieldGroup">
            <label
              :class="$style.label"
              for="bank_id"
            >
              {{ $t('account.bank_section.bank') }}
            </label>
            <BaseFormSelectField
              id="bank_id"
              v-model="bankAccountData.bank_id"
              :options="bankOptions"
              placeholder="Selecione o banco"
            />
          </div>

          <div :class="$style.fieldGroup">
            <label
              :class="$style.label"
              for="type"
            >
              {{ $t('account.bank_section.account_type') }}
            </label>
            <BaseFormSelectField
              id="type"
              v-model="bankAccountData.type"
              class="w-28"
              :options="accountTypeOptions"
              placeholder="Tipo de conta"
            />
          </div>
        </section>

        <section :class="$style.accountFieldsRow">
          <div :class="$style.accountFieldGroup">
            <label :class="$style.label">
              {{ $t('account.bank_section.agency') }}
            </label>

            <div :class="$style.inputWithDigit">
              <BaseFormInput
                id="agency"
                v-model="bankAccountData.agency"
                :class="$style.mainInput"
                inputmode="numeric"
                :maxlength="4"
                type="tel"
                @input="sanitizeInput"
              />

              <div :class="$style.digitGroup">
                <label :class="$style.digitLabel">
                  {{ $t('account.bank_section.number_digit') }}
                </label>
                <BaseFormInput
                  id="agency_digit"
                  v-model="bankAccountData.agency_digit"
                  :class="$style.digitInput"
                  inputmode="numeric"
                  :maxlength="1"
                  type="tel"
                  @input="sanitizeInput"
                />
              </div>
            </div>
          </div>

          <div :class="$style.accountFieldGroup">
            <label :class="$style.label">
              {{ $t('account.bank_section.account_number') }}
            </label>

            <div :class="$style.inputWithDigit">
              <BaseFormInput
                id="number"
                v-model="bankAccountData.number"
                :class="$style.mainInput"
                inputmode="numeric"
                :maxlength="10"
                type="tel"
                @input="sanitizeInput"
              />

              <div :class="$style.digitGroup">
                <label :class="$style.digitLabel">
                  {{ $t('account.bank_section.number_digit') }}
                </label>
                <BaseFormInput
                  id="number_digit"
                  v-model="bankAccountData.number_digit"
                  :class="$style.digitInput"
                  inputmode="numeric"
                  :maxlength="2"
                  type="tel"
                  @input="sanitizeInput"
                />
              </div>
            </div>
          </div>
        </section>

        <div
          :class="$style.alert"
          v-html="
            $t('account.bank_section.bank_info', { cpf: cpfMask(userDocument) })
          "
        />
      </section>

      <AuthVerificationBoxProceed
        ref="proceedButtonRef"
        :class="$style.proceedButton"
        :disabled="isProceedDisabled || !formIsValid"
        label="Finalizar Cadastro"
        @submit="handleSubmit"
      />
    </div>
  </AuthVerificationBox>
</template>

<style module lang="scss">
.container {
  @apply flex flex-col gap-1 -mt-10;
}

.sectionContainer {
  @apply mt-10;
}

.fieldsRow {
  @apply flex gap-3;
}

.fieldGroup {
  @apply flex-grow;
}

.accountFieldsRow {
  @apply flex gap-4 mt-4 items-start;
}

.accountFieldGroup {
  @apply flex flex-col flex-1;
}

.inputWithDigit {
  @apply flex gap-2 items-end;
}

.mainInput {
  @apply flex-1;
}

.digitGroup {
  @apply flex flex-col items-center;
}

.digitLabel {
  @apply text-xs font-medium text-texts/70 flex items-center -mt-4;
}

.digitInput {
  @apply w-12 text-center;
}

.label {
  @apply text-xs font-medium text-texts/70 flex items-center;
}

.alert {
  @apply text-warning text-xs mt-6 px-0.5 w-full;

  strong {
    @apply font-bold whitespace-nowrap;
  }
}

.proceedButton {
  @apply mt-4;
}
</style>
